import React from "react";
import { motion } from "framer-motion";
import { FaSquareUpwork } from "react-icons/fa6";
import { TbBrandFiverr } from "react-icons/tb";
import { testimonials } from "../constant/data";

// Convert ISO 2-letter country code → flag emoji
const flagEmoji = (code) => {
  if (!code || code.length !== 2) return "";
  const A = 0x1f1e6;
  const offset = "A".charCodeAt(0);
  const cc = code.toUpperCase();
  return String.fromCodePoint(A + cc.charCodeAt(0) - offset, A + cc.charCodeAt(1) - offset);
};

const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5">
    {[...Array(5)].map((_, i) => (
      <svg
        key={i}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill={i < n ? "#fbbf24" : "rgba(255,255,255,0.15)"}
      >
        <path d="M12 2l2.95 6.6 7.05.7-5.3 4.95L18 22l-6-3.6L6 22l1.3-7.75L2 9.3l7.05-.7z" />
      </svg>
    ))}
  </div>
);

const SourceBadge = ({ source }) => {
  if (source === "Upwork") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-300/90">
        <FaSquareUpwork className="w-3.5 h-3.5" />
        via Upwork
      </span>
    );
  }
  if (source === "Fiverr") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-aurora-mint">
        <TbBrandFiverr className="w-3.5 h-3.5" />
        via Fiverr
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-white/50">
      Direct client
    </span>
  );
};

const Card = ({ t }) => (
  <article className="flex-shrink-0 w-[88vw] sm:w-[420px] md:w-[460px] mr-4 md:mr-5">
    <div className="relative h-full min-h-[230px] rounded-2xl border border-white/10 p-5 md:p-6 overflow-hidden bg-white/[0.03] flex flex-col">
      <div
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-aurora-teal/15 blur-2xl pointer-events-none"
        aria-hidden
      />
      <div className="relative flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-3">
          <Stars n={t.rating} />
          <SourceBadge source={t.source} />
        </div>

        <p className="mt-4 text-sm md:text-[15px] text-white/85 leading-relaxed flex-1">
          “{t.quote}”
        </p>

        <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-aurora-teal/40 to-aurora-magenta/40 flex items-center justify-center text-xs text-white font-semibold uppercase">
              {t.name.charAt(0)}
            </div>
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{t.name}</div>
              {t.country && (
                <div className="text-[11px] text-white/50 truncate flex items-center gap-1.5">
                  <span>Country: {t.country}</span>
                  {t.countryCode && (
                    <span className="text-sm leading-none" aria-label={t.country}>
                      {flagEmoji(t.countryCode)}
                    </span>
                  )}
                </div>
              )}
              {t.timeAgo && (
                <div className="text-[11px] text-white/40 truncate">
                  {t.timeAgo}
                </div>
              )}
            </div>
          </div>

          {t.duration && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[11px] px-2 py-1 rounded-full bg-white/[0.05] border border-white/10 text-white/70 whitespace-nowrap">
                {t.duration}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  </article>
);

const Row = ({ items, reverse = false, durationSec = 80 }) => (
  <div
    className="flex marquee"
    style={{
      animationDuration: `${durationSec}s`,
      animationDirection: reverse ? "reverse" : "normal",
    }}
  >
    {[...items, ...items].map((t, i) => (
      <Card key={`${t.id}-${i}`} t={t} />
    ))}
  </div>
);

const TestimonialsSection = () => {
  const total = testimonials.length;
  // Two rows, alternating, longer marquee duration scaled to count
  const half = Math.ceil(total / 2);
  const rowA = testimonials.slice(0, half);
  const rowB = testimonials.slice(half);

  // ~6s per card so it never feels rushed
  const speed = Math.max(60, half * 6);

  return (
    <section id="reviews" className="relative w-full py-20 md:py-28 overflow-hidden">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[60vw] h-[40vh] bg-aurora-amber/5 blur-[140px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mb-12 md:mb-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="eyebrow">
              <span className="dot" />
              Client reviews
            </span>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-5 font-jamjuree text-3xl md:text-5xl font-semibold text-white leading-tight"
            >
              Words from clients we've{" "}
              <span className="text-gradient">shipped for</span>.
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex items-center gap-5 shrink-0"
          >
            <div className="flex flex-col items-end text-right">
              <div className="flex items-center gap-1">
                <Stars n={5} />
                <span className="ml-1 text-sm text-white/85 font-semibold">
                  5.0
                </span>
              </div>
              <span className="text-[11px] text-white/50 uppercase tracking-[0.2em] mt-1 whitespace-nowrap">
                {testimonials.length} verified reviews
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <a
                href="https://www.fiverr.com/hamzaamehboob"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 text-aurora-mint hover:border-aurora-teal/40 transition-colors"
                aria-label="See Fiverr profile"
              >
                <TbBrandFiverr className="w-5 h-5" />
              </a>
              <a
                href="https://www.upwork.com/freelancers/~01efd5ce631be90f37"
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center bg-white/[0.04] border border-white/10 text-emerald-300 hover:border-emerald-300/40 transition-colors"
                aria-label="See Upwork profile"
              >
                <FaSquareUpwork className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Marquee rows with edge fade */}
      <div id="reviews-rows" className="relative scroll-mt-32">
        <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 z-10 pointer-events-none bg-gradient-to-r from-ink-950 to-transparent" />
        <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 z-10 pointer-events-none bg-gradient-to-l from-ink-950 to-transparent" />

        <div className="space-y-5">
          <Row items={rowA} durationSec={speed} />
          <Row items={rowB} reverse durationSec={speed + 10} />
        </div>
      </div>

      {/* CTA below the rows */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mt-12 md:mt-14 flex justify-center">
        <a
          href="https://www.fiverr.com/hamzaamehboob"
          target="_blank"
          rel="noreferrer"
          className="btn-ghost"
        >
          Read all reviews on Fiverr
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M7 17 17 7M17 7H8M17 7v9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      </div>
    </section>
  );
};

export default TestimonialsSection;
